import { useMemo } from 'react';

import { coinKey, useTradingStore } from '../store';
import type { PositionType } from '../types';

/**
 * The resting limit order on the charted market, in one shape whichever mode
 * produced it.
 *
 * Real orders live on Hyperliquid and reach the store as the selected market's
 * `resting` slot (so the flat `exec*` projection already IS this market's
 * order); demo orders live in `demoRestingOrders`. The rail, the mobile panel
 * and the chart all want the same six facts, so they ask here rather than each
 * re-deriving which mode they are in.
 */
export interface RestingOrderView {
    coin: string;
    type: PositionType;
    /**
     * The price the order rests at, or null when the slot holds an order whose
     * price this client has not learned yet (a resting row restored without
     * one). Null renders as a dash — it must NOT collapse the card back to the
     * order form, because the pair's slot is held either way.
     */
    limitPrice: number | null;
    /** collateral committed to the order; null when the backend has not said. */
    amount: number | null;
    leverage: number | null;
    /** when the order was placed (ms epoch), for the "resting since" readout. */
    placedAt: number | null;
}

export function useRestingOrder(): RestingOrderView | null {
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const selectedCoin = useTradingStore((s) => s.selectedCoin);
    const executionStatus = useTradingStore((s) => s.executionStatus);
    const execLimitPrice = useTradingStore((s) => s.execLimitPrice);
    const execPositionType = useTradingStore((s) => s.execPositionType);
    const execPositionAmount = useTradingStore((s) => s.execPositionAmount);
    const execLeverage = useTradingStore((s) => s.execLeverage);
    const execOpenedAt = useTradingStore((s) => s.execOpenedAt);
    // the stored object itself, so this selector is reference-stable between
    // unchanged reads (it is read through useSyncExternalStore).
    const demoOrder = useTradingStore((s) => {
        const key = coinKey(s.selectedCoin);
        return key === null ? undefined : s.demoRestingOrders[key];
    });

    return useMemo(() => {
        if (!isRealMode) {
            if (!demoOrder || selectedCoin === null) return null;
            return {
                coin: selectedCoin,
                type: demoOrder.type,
                limitPrice: demoOrder.limitPrice,
                amount: demoOrder.amount,
                leverage: demoOrder.leverage,
                placedAt: demoOrder.placedAt,
            };
        }

        if (executionStatus !== 'resting' || selectedCoin === null) return null;
        const price = execLimitPrice !== null ? Number(execLimitPrice) : NaN;
        // A slot with no usable price still HOLDS the pair — returning null here
        // would render the order form over a live order and invite a second one.
        // The card shows a dash for the price instead.
        const usable = Number.isFinite(price) && price > 0;
        return {
            coin: selectedCoin,
            type: execPositionType ?? 'LONG',
            limitPrice: usable ? price : null,
            amount: execPositionAmount,
            leverage: execLeverage,
            placedAt: execOpenedAt,
        };
    }, [
        isRealMode,
        demoOrder,
        selectedCoin,
        executionStatus,
        execLimitPrice,
        execPositionType,
        execPositionAmount,
        execLeverage,
        execOpenedAt,
    ]);
}
